'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileStack,
  History,
  LayoutList,
  Loader2,
  Plus,
  Sparkles
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

import { ocrApi } from '../services/ocr.api';
import { ocrKeys, ocrQueries } from '../services/ocr.queries';
import type {
  OcrSuggestionRunDetail,
  OcrSuggestionRunSummary,
  OcrSuggestionRunType,
  PageTemplateSuggestion,
  PoolSuggestionEntry
} from '../types';

interface SuggestionHistoryPanelProps {
  /** Provide one of these. registrationId is preferred; containerId resolves it server-side. */
  registrationId?: number;
  containerId?: number;
  /** Restrict the list to one run type (per-page tab / pool tab); omit to show all. */
  filterType?: OcrSuggestionRunType;
  mode: 'interactive' | 'readonly';
  /** Only fetch while visible. */
  enabled?: boolean;
  /** Interactive: apply a past run to the current editor. */
  onUse?: (run: OcrSuggestionRunDetail) => void;
  /**
   * Mode-agnostic: replay this run's frozen highlights/extracted data into the
   * main results panel. Fires once the user has explicitly navigated/picked a
   * run (never for the initial auto-selected newest run) — mirrors the guard
   * `onUse` already uses in interactive mode, just independent of `mode`.
   */
  onPreview?: (run: OcrSuggestionRunDetail) => void;
  /** Interactive: generate a fresh run ("+ new"). */
  onNew?: () => void;
  regenerating?: boolean;
  /** Render as a collapsed-by-default section (read-only surfaces) to save space. */
  collapsible?: boolean;
}

/**
 * ChatGPT-style run history: a single run is rendered at a time and a
 * ‹ prev / next › pager steps between runs. Newest is shown first; paging left
 * walks back through older runs. In interactive mode, landing on (or clicking)
 * a run renders it into the editor — except the initial auto-selected newest
 * run, which is shown but not auto-applied so it never clobbers in-progress
 * picks on open.
 */
export function SuggestionHistoryPanel({
  registrationId,
  containerId,
  filterType,
  mode,
  enabled = true,
  onUse,
  onPreview,
  onNew,
  regenerating,
  collapsible = false
}: SuggestionHistoryPanelProps) {
  const t = useTranslations('ocr');
  const [expanded, setExpanded] = useState(!collapsible);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);

  // Apply-on-navigate guards. `hasNavigated` stays false until the user pages
  // or clicks a run, so the newest run auto-selected on open is rendered but
  // not applied. `lastApplied`/`lastPreviewed` de-dupe their respective
  // effects across re-renders (onUse/onPreview from the parent are fresh
  // closures each render, so they live in refs to keep them out of the effect
  // deps).
  const hasNavigatedRef = useRef(false);
  const lastAppliedRef = useRef<number | null>(null);
  const lastPreviewedRef = useRef<number | null>(null);
  const onUseRef = useRef(onUse);
  useEffect(() => {
    onUseRef.current = onUse;
  }, [onUse]);
  const onPreviewRef = useRef(onPreview);
  useEffect(() => {
    onPreviewRef.current = onPreview;
  }, [onPreview]);

  // Address the list by registration when known, else by container. Built as a
  // single options object (a union of queryOptions confuses useQuery's
  // queryKey inference).
  const regId = registrationId ?? 0;
  const contId = containerId ?? 0;
  const byRegistration = regId > 0;
  const runsQuery = useQuery({
    queryKey: byRegistration
      ? ocrKeys.suggestionRuns(regId)
      : ocrKeys.containerSuggestionRuns(contId),
    queryFn: () =>
      byRegistration
        ? ocrApi.getSuggestionRuns(regId)
        : ocrApi.getContainerSuggestionRuns(contId),
    enabled: enabled && (byRegistration || contId > 0)
  });

  // API returns newest-first; reverse to ChatGPT order so position 1 is the
  // oldest run and position N is the newest (the default selection).
  const runs = useMemo(
    () =>
      (runsQuery.data?.data ?? [])
        .filter((run) => !filterType || run.type === filterType)
        .reverse(),
    [runsQuery.data, filterType]
  );

  const total = runs.length;

  // Default to the newest run (last in ChatGPT order) until the user pages to
  // a specific one. The fallback also covers a selected run dropping out of
  // the list. No effect needed: a new run never lands while the popover is open
  // (interactive "New" closes it; read-only surfaces never regenerate).
  const selectedIndex = useMemo(() => {
    const i = runs.findIndex((r) => r.id === selectedRunId);
    return i >= 0 ? i : total - 1;
  }, [runs, selectedRunId, total]);

  const selectedRun = selectedIndex >= 0 ? runs[selectedIndex] : null;

  const detailQuery = useQuery(
    ocrQueries.suggestionRun(selectedRun?.id ?? 0, selectedRun != null)
  );
  const detail = detailQuery.data?.data;

  // Apply the loaded run to the editor once the user has navigated/picked.
  useEffect(() => {
    if (mode !== 'interactive' || !detail) return;
    if (!hasNavigatedRef.current) {
      // Suppress the initial auto-selected newest run.
      lastAppliedRef.current = detail.id;
      return;
    }
    if (lastAppliedRef.current === detail.id) return;
    lastAppliedRef.current = detail.id;
    onUseRef.current?.(detail);
  }, [detail, mode]);

  // Replay the loaded run into the main panel once the user has navigated —
  // independent of `mode`, so it fires alongside (not instead of) the
  // interactive apply-to-editor effect above.
  useEffect(() => {
    if (!detail || !hasNavigatedRef.current) return;
    if (lastPreviewedRef.current === detail.id) return;
    lastPreviewedRef.current = detail.id;
    onPreviewRef.current?.(detail);
  }, [detail]);

  const goTo = (index: number) => {
    if (total === 0) return;
    const clamped = Math.max(0, Math.min(total - 1, index));
    hasNavigatedRef.current = true;
    setSelectedRunId(runs[clamped]?.id ?? null);
  };

  const applyCurrent = () => {
    if (mode !== 'interactive' || !detail) return;
    hasNavigatedRef.current = true;
    lastAppliedRef.current = detail.id;
    onUseRef.current?.(detail);
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between gap-2'>
        {collapsible ? (
          <button
            type='button'
            onClick={() => setExpanded((prev) => !prev)}
            className='flex items-center gap-2 text-sm font-medium'
          >
            <History className='h-4 w-4' />
            {t('historyTitle')}
            {total > 0 ? (
              <span className='text-muted-foreground'>({total})</span>
            ) : null}
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                !expanded && '-rotate-90'
              )}
            />
          </button>
        ) : (
          <div className='flex items-center gap-2 text-sm font-medium'>
            <History className='h-4 w-4' />
            {t('historyTitle')}
          </div>
        )}
        {mode === 'interactive' && onNew ? (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onNew}
            disabled={regenerating}
          >
            {regenerating ? (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            ) : (
              <Plus className='me-2 h-4 w-4' />
            )}
            {t('historyNew')}
          </Button>
        ) : null}
      </div>

      {expanded &&
        (runsQuery.isPending ? (
          <div className='text-muted-foreground flex items-center gap-2 py-4 text-xs'>
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
            {t('loadingHistory')}
          </div>
        ) : total === 0 ? (
          <p className='text-muted-foreground py-4 text-center text-xs'>
            {t('historyEmpty')}
          </p>
        ) : (
          <>
            {total > 1 ? (
              <div className='flex items-center justify-center gap-2'>
                <Button
                  type='button'
                  size='icon'
                  variant='ghost'
                  className='h-7 w-7'
                  disabled={selectedIndex <= 0}
                  onClick={() => goTo(selectedIndex - 1)}
                  aria-label={t('historyPrevRun')}
                >
                  <ChevronLeft className='h-4 w-4 rtl:rotate-180' />
                </Button>
                <span className='text-muted-foreground text-xs tabular-nums'>
                  {selectedIndex + 1} / {total}
                </span>
                <Button
                  type='button'
                  size='icon'
                  variant='ghost'
                  className='h-7 w-7'
                  disabled={selectedIndex >= total - 1}
                  onClick={() => goTo(selectedIndex + 1)}
                  aria-label={t('historyNextRun')}
                >
                  <ChevronRight className='h-4 w-4 rtl:rotate-180' />
                </Button>
              </div>
            ) : null}

            {selectedRun ? (
              <SelectedRunCard
                run={selectedRun}
                detail={detail}
                loading={detailQuery.isFetching && !detail}
                interactive={mode === 'interactive'}
                previewSupported={onPreview != null}
                onApply={applyCurrent}
              />
            ) : null}
          </>
        ))}
    </div>
  );
}

function SelectedRunCard({
  run,
  detail,
  loading,
  interactive,
  previewSupported,
  onApply
}: {
  run: OcrSuggestionRunSummary;
  detail: OcrSuggestionRunDetail | undefined;
  loading: boolean;
  interactive: boolean;
  previewSupported: boolean;
  onApply: () => void;
}) {
  const t = useTranslations('ocr');

  const meta = (
    <div className='flex flex-col gap-0.5'>
      <div className='flex w-full items-center justify-between gap-2'>
        <span className='flex items-center gap-1.5 text-xs font-medium'>
          {run.type === 'per_page' ? (
            <LayoutList className='h-3.5 w-3.5' />
          ) : (
            <FileStack className='h-3.5 w-3.5' />
          )}
          {run.type === 'per_page'
            ? t('historyPerPageLabel', { count: run.item_count })
            : t('historyPoolLabel', { count: run.item_count })}
        </span>
        {run.has_assignment ? (
          <Badge variant='secondary' className='gap-1'>
            <CheckCircle2 className='h-3 w-3' />
            {t('historyRan')}
          </Badge>
        ) : (
          <Badge variant='outline'>{t('historySuggested')}</Badge>
        )}
      </div>
      <span className='text-muted-foreground text-xs'>
        {[run.generated_by, formatWhen(run.ran_at ?? run.created_at)]
          .filter(Boolean)
          .join(' · ')}
      </span>
    </div>
  );

  const body = (
    <div className='mt-2'>
      {loading ? (
        <div className='text-muted-foreground flex items-center gap-2 py-2 text-xs'>
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
        </div>
      ) : detail ? (
        <>
          <ScrollArea className='max-h-44'>
            <RunDetail detail={detail} />
          </ScrollArea>
          {previewSupported && (detail.ocr_results?.length ?? 0) === 0 && (
            <p className='text-muted-foreground mt-2 text-xs'>
              {t('noHistoricalData')}
            </p>
          )}
        </>
      ) : null}
    </div>
  );

  if (interactive) {
    return (
      <div
        role='button'
        tabIndex={0}
        onClick={onApply}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onApply();
          }
        }}
        className='hover:border-primary focus-visible:ring-ring cursor-pointer rounded-md border p-3 text-start transition-colors outline-none focus-visible:ring-2'
      >
        {meta}
        {body}
        <div className='mt-2 flex items-center gap-1.5 text-xs text-amber-600'>
          <Sparkles className='h-3.5 w-3.5' />
          {t('historyUse')}
        </div>
      </div>
    );
  }

  return (
    <div className='rounded-md border p-3'>
      {meta}
      {body}
    </div>
  );
}

function RunDetail({ detail }: { detail: OcrSuggestionRunDetail }) {
  const t = useTranslations('ocr');

  if (detail.type === 'pool') {
    const suggestions = (
      Array.isArray(detail.suggestions) ? detail.suggestions : []
    ) as PoolSuggestionEntry[];
    const chosen = new Set(
      Array.isArray(detail.assignment) ? detail.assignment : []
    );

    if (suggestions.length === 0) {
      return (
        <span className='text-muted-foreground text-xs'>
          {t('historyChosenCount', { count: chosen.size })}
        </span>
      );
    }

    return (
      <div className='space-y-1'>
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.template_id}
            className='flex items-center justify-between gap-2 text-xs'
          >
            <span
              className={cn(
                'truncate',
                chosen.has(suggestion.template_id) && 'text-primary font-medium'
              )}
            >
              {chosen.has(suggestion.template_id) ? '✓ ' : ''}
              {suggestion.name}
            </span>
            <SimilarityPct value={suggestion.similarity} />
          </div>
        ))}
      </div>
    );
  }

  // per_page — render each page's chosen (or top-ranked) template so the run
  // is fully visible, not just a count.
  const suggestions =
    detail.suggestions && !Array.isArray(detail.suggestions)
      ? (detail.suggestions as Record<number, PageTemplateSuggestion[]>)
      : {};
  const assignment =
    detail.assignment && !Array.isArray(detail.assignment)
      ? (detail.assignment as Record<number, number>)
      : {};

  const pageIds = Object.keys(suggestions).length
    ? Object.keys(suggestions)
    : Object.keys(assignment);

  if (pageIds.length === 0) {
    return (
      <span className='text-muted-foreground text-xs'>
        {t('historyPerPageDetail', {
          pages: detail.item_count,
          assigned: Object.keys(assignment).length
        })}
      </span>
    );
  }

  return (
    <div className='space-y-1'>
      {pageIds.map((pageId, index) => {
        const ranked = suggestions[Number(pageId)] ?? [];
        const assignedId = assignment[Number(pageId)];
        const isAssigned = assignedId != null;
        // Assigned → that template (may be a manual pick absent from the ranked
        // list, in which case we have no name to show); otherwise the top pick.
        const chosen = isAssigned
          ? ranked.find((s) => s.template_id === assignedId)
          : ranked[0];
        return (
          <div
            key={pageId}
            className='flex items-center justify-between gap-2 text-xs'
          >
            <span className='text-muted-foreground shrink-0'>
              {t('pageLabel', { number: index + 1 })}
            </span>
            <span
              className={cn(
                'flex-1 truncate text-end',
                isAssigned && 'text-primary font-medium'
              )}
            >
              {isAssigned ? '✓ ' : ''}
              {chosen?.name ?? '—'}
            </span>
            {chosen ? <SimilarityPct value={chosen.similarity} /> : null}
          </div>
        );
      })}
    </div>
  );
}

function SimilarityPct({ value }: { value: number }) {
  const percent = Math.max(0, Math.min(100, Math.round(value * 100)));
  const tone =
    percent >= 75
      ? 'text-emerald-600'
      : percent >= 50
        ? 'text-amber-600'
        : 'text-muted-foreground';
  return (
    <span className={cn('text-[10px] font-semibold', tone)}>{percent}%</span>
  );
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return '';
  }
}
